import React, { useMemo } from "react";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";

// Section and category display order
const SECTION_ORDER = ["Business", "Personal", "Income"];
const CATEGORY_ORDER = ["Child", "Other", "Apartment", "Bills", "Health", "Transport", "Subscriptions"];

// Helper to find the mode (most common value) in an array
const getModeValue = (values) => {
	const counts = {};
	values.forEach((v) => {
		const key = String(v);
		counts[key] = (counts[key] || 0) + 1;
	});
	let maxCount = 0;
	let mode = values[0];
	Object.entries(counts).forEach(([key, count]) => {
		if (count > maxCount) {
			maxCount = count;
			mode = parseFloat(key) || 0;
		}
	});
	return mode;
};

// Check if value differs from mode (is a standout)
const isStandout = (value, mode) => {
	return value !== mode && value !== 0;
};

/**
 * Main budget data table with collapsible sections and categories.
 * Shows expense sections, income sections, and totals.
 * Includes year total columns between December and January.
 */
const BudgetDataTable = ({
	months,
	groupedItems,
	collapsedSections,
	collapsedCategories,
	toggleSection,
	toggleCategory,
	variableItems,
	variableValues,
	toggleVariableItem,
	setVariableValue,
	getEffectiveItemValue,
	getEffectiveGross,
	isUsingEstimate,
	getCalculatedTotals,
	startingBalance,
	businessExpenses,
	getBusinessExpenses = (dateKey) => businessExpenses,
	taxRate,
	formatCurrency,
	scrollRef,
	onScroll,
	actualSpendingByMonth = {},
	isMonthClosed = () => false,
	isCurrentMonth = () => false,
	shouldShowActuals = () => false,
	historicalIncomeByMonth = {},
	historicalExpensesByMonth = {},
	invoicesByMonth = {},
	monthProgress = 0,
	getLiveItemData = () => ({ hasData: false, actual: 0, remaining: 0 }),
	networthByMonth = {},
}) => {
	// Calculate remaining percentage for current month proration
	const monthRemaining = 1 - monthProgress;
	// Get actual spending for a closed or current month, or null if future
	const getActualSpending = (dateKey, business) => {
		if (!shouldShowActuals(dateKey)) return null;
		return actualSpendingByMonth[business]?.[dateKey] || 0;
	};

	// Get Erin income for a closed or current month
	const getErinIncome = (dateKey) => {
		if (!shouldShowActuals(dateKey)) return null;
		return actualSpendingByMonth.ErinIncome?.[dateKey] || 0;
	};

	// Calculate actual totals for closed months
	const getActualTotals = (dateKey) => {
		if (!isMonthClosed(dateKey)) return null;
		const home = actualSpendingByMonth.Home?.[dateKey] || 0;
		const apartment = actualSpendingByMonth.Apartment?.[dateKey] || 0;
		const other = actualSpendingByMonth.Other?.[dateKey] || 0;
		const erinIncome = actualSpendingByMonth.ErinIncome?.[dateKey] || 0;
		return {
			expenses: -(home + apartment + other),
			erinIncome,
			home,
			apartment,
			other,
		};
	};

	// Get actual business takehome for closed or current months
	// Current month uses invoice data, historical months use legacy tables
	const getActualTakehome = (dateKey) => {
		if (!shouldShowActuals(dateKey)) return null;

		// For current month, use invoice-based calculation
		if (isCurrentMonth(dateKey)) {
			const invoiceData = invoicesByMonth[dateKey] || { total: 0 };
			const gross = invoiceData.total;
			const net = gross - businessExpenses;
			const taxes = net > 0 ? net * taxRate : 0;
			return net - taxes;
		}

		// For closed months, use historical data
		const gross = historicalIncomeByMonth[dateKey] || 0;
		const expenses = historicalExpensesByMonth[dateKey] || 0;
		const net = gross - expenses;
		const taxes = net > 0 ? net * taxRate : 0;
		return net - taxes;
	};

	// Helper to get the appropriate class for actual values (pending for current month)
	const getActualClass = (dateKey) => {
		if (isCurrentMonth(dateKey)) return "budget-forecast__td-value--pending";
		return "budget-forecast__td-value--actual";
	};

	// Helper to prorate values for current month (show only remaining portion)
	const getProratedValue = (value, dateKey) => {
		if (isCurrentMonth(dateKey)) {
			return value * monthRemaining;
		}
		return value;
	};

	// Helper to get CSS class for current month column (in-progress styling)
	const getInProgressClass = (dateKey) => {
		if (isCurrentMonth(dateKey)) return "budget-forecast__td-value--in-progress";
		return "";
	};

	// Helper to get CSS class for current month header
	const getInProgressHeaderClass = (dateKey) => {
		if (isCurrentMonth(dateKey)) return "budget-forecast__th-month--in-progress";
		return "";
	};
	// Build columns with year totals inserted after each year
	const columns = useMemo(() => {
		const cols = [];
		let currentYear = null;
		let yearMonths = [];

		months.forEach((m, i) => {
			const year = m.dateKey.split("-")[0];

			// If year changed, insert year total for previous year
			if (currentYear && year !== currentYear) {
				cols.push({
					type: "year-total",
					year: currentYear,
					months: [...yearMonths],
				});
				yearMonths = [];
			}

			currentYear = year;
			yearMonths.push(m);
			cols.push({
				type: "month",
				month: m,
				index: i,
			});
		});

		// Add final year total
		if (yearMonths.length > 0) {
			cols.push({
				type: "year-total",
				year: currentYear,
				months: yearMonths,
			});
		}

		return cols;
	}, [months]);

	// Helper to calculate year total for items
	const getYearItemTotal = (yearMonths, items) => {
		return yearMonths.reduce((sum, m) => {
			return sum + items.reduce((itemSum, item) => itemSum + getEffectiveItemValue(item, m.dateKey), 0);
		}, 0);
	};

	// Helper to calculate year total for calculated totals
	const getYearCalculatedTotal = (yearMonths, field) => {
		return yearMonths.reduce((sum, m) => {
			const calculated = getCalculatedTotals(m.dateKey);
			return sum + calculated[field];
		}, 0);
	};
	return (
		<div className="budget-forecast__table-wrapper" ref={scrollRef} onScroll={onScroll}>
			<table className="budget-forecast__table">
				<thead>
					<tr>
						<th className="budget-forecast__th-name">Item</th>
						{columns.map((col) => (
							col.type === "month" ? (
								<th key={col.month.dateKey} className={`budget-forecast__th-month ${col.index % 2 === 1 ? "budget-forecast__th-month--alt" : ""} ${getInProgressHeaderClass(col.month.dateKey)}`}>
									{col.month.label}
									{isCurrentMonth(col.month.dateKey) && <span className="budget-forecast__th-progress">({Math.round(monthRemaining * 100)}%)</span>}
								</th>
							) : (
								<th key={`year-${col.year}`} className="budget-forecast__th-month budget-forecast__th-year">
									{col.year}
								</th>
							)
						))}
					</tr>
				</thead>
				<tbody>
					{/* Expense Sections */}
					{Object.entries(groupedItems)
						.filter(([_, data]) => !data.isIncome)
						.sort(([a], [b]) => {
							const aIndex = SECTION_ORDER.indexOf(a);
							const bIndex = SECTION_ORDER.indexOf(b);
							return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
						})
						.map(([section, sectionData]) => (
							<React.Fragment key={section}>
								{/* Section Row */}
								<tr
									className={`budget-forecast__row budget-forecast__row--section ${collapsedSections[section] ? "budget-forecast__row--collapsed" : ""}`}
									onClick={() => toggleSection(section)}
								>
									<td className="budget-forecast__td-name">
										{collapsedSections[section] ? <FiChevronRight /> : <FiChevronDown />}
										{section}
									</td>
									{columns.map((col) => {
										if (col.type === "month") {
											if (isMonthClosed(col.month.dateKey)) {
												return (
													<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
														—
													</td>
												);
											}
											// Calculate section total, accounting for large-ticket live data
											// Large-ticket items with live data should NOT be prorated (already actual remaining)
											let regularTotal = 0;
											let largeTicketTotal = 0;
											Object.values(sectionData.categories).flat().forEach((item) => {
												const forecastValue = getEffectiveItemValue(item, col.month.dateKey);
												if (item.is_large_ticket) {
													const liveData = getLiveItemData(item.id, col.month.dateKey, forecastValue);
													if (liveData.hasData) {
														largeTicketTotal += forecastValue >= 0 ? liveData.remaining : -liveData.remaining;
													} else {
														regularTotal += forecastValue;
													}
												} else {
													regularTotal += forecastValue;
												}
											});
											const displayValue = getProratedValue(regularTotal, col.month.dateKey) + largeTicketTotal;
											return (
												<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}>
													{formatCurrency(displayValue)}
												</td>
											);
										} else {
											// For year totals, account for large-ticket live data
											const allItems = Object.values(sectionData.categories).flat();
											const yearTotal = col.months
												.filter(m => !isMonthClosed(m.dateKey))
												.reduce((sum, m) => {
													return sum + allItems.reduce((itemSum, item) => {
														const forecastValue = getEffectiveItemValue(item, m.dateKey);
														if (item.is_large_ticket) {
															const liveData = getLiveItemData(item.id, m.dateKey, forecastValue);
															return itemSum + (liveData.hasData ? (forecastValue >= 0 ? liveData.remaining : -liveData.remaining) : forecastValue);
														}
														return itemSum + forecastValue;
													}, 0);
												}, 0);
											return (
												<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
													{yearTotal !== 0 ? formatCurrency(yearTotal) : "—"}
												</td>
											);
										}
									})}
								</tr>

								{/* Categories and Items */}
								{!collapsedSections[section] &&
									Object.entries(sectionData.categories)
										.sort(([a], [b]) => {
											const aIndex = CATEGORY_ORDER.indexOf(a);
											const bIndex = CATEGORY_ORDER.indexOf(b);
											return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
										})
										.map(([category, items]) => {
											// Calculate mode for each item's values for standout detection
											const itemModes = items.map((item) => {
												const values = months.map((m) => getEffectiveItemValue(item, m.dateKey));
												return { item, mode: getModeValue(values) };
											});

											return (
												<React.Fragment key={`${section}-${category}`}>
													{/* Category Row */}
													<tr
														className={`budget-forecast__row budget-forecast__row--category ${collapsedCategories[`${section}-${category}`] ? "budget-forecast__row--collapsed" : ""}`}
														onClick={() => toggleCategory(`${section}-${category}`)}
													>
														<td className="budget-forecast__td-name">
															{collapsedCategories[`${section}-${category}`] ? <FiChevronRight /> : <FiChevronDown />}
															{category}
														</td>
														{columns.map((col) => {
															if (col.type === "month") {
																if (isMonthClosed(col.month.dateKey)) {
																	return (
																		<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
																			—
																		</td>
																	);
																}
																// Calculate category total, accounting for large-ticket live data
																// Large-ticket items with live data should NOT be prorated (already actual remaining)
																let regularTotal = 0;
																let largeTicketTotal = 0;
																items.forEach((item) => {
																	const forecastValue = getEffectiveItemValue(item, col.month.dateKey);
																	if (item.is_large_ticket) {
																		const liveData = getLiveItemData(item.id, col.month.dateKey, forecastValue);
																		if (liveData.hasData) {
																			largeTicketTotal += forecastValue >= 0 ? liveData.remaining : -liveData.remaining;
																		} else {
																			regularTotal += forecastValue;
																		}
																	} else {
																		regularTotal += forecastValue;
																	}
																});
																const displayValue = getProratedValue(regularTotal, col.month.dateKey) + largeTicketTotal;
																return (
																	<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}>
																		{formatCurrency(displayValue)}
																	</td>
																);
															} else {
																// Year total accounting for large-ticket live data
																const yearTotal = col.months
																	.filter(m => !isMonthClosed(m.dateKey))
																	.reduce((sum, m) => {
																		return sum + items.reduce((itemSum, item) => {
																			const forecastValue = getEffectiveItemValue(item, m.dateKey);
																			if (item.is_large_ticket) {
																				const liveData = getLiveItemData(item.id, m.dateKey, forecastValue);
																				return itemSum + (liveData.hasData ? (forecastValue >= 0 ? liveData.remaining : -liveData.remaining) : forecastValue);
																			}
																			return itemSum + forecastValue;
																		}, 0);
																	}, 0);
																return (
																	<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
																		{yearTotal !== 0 ? formatCurrency(yearTotal) : "—"}
																	</td>
																);
															}
														})}
													</tr>

													{/* Item Rows */}
													{!collapsedCategories[`${section}-${category}`] &&
														itemModes.map(({ item, mode }) => {
															const isVariable = variableItems.has(item.id);
															const currentValue = variableValues[item.id] ?? item.monthlyValues[months[0]?.dateKey] ?? 0;
															return (
																<tr
																	key={item.id}
																	className={`budget-forecast__row budget-forecast__row--item ${isVariable ? "budget-forecast__row--variable" : ""}`}
																>
																	<td className="budget-forecast__td-name">
																		<input
																			type="checkbox"
																			checked={isVariable}
																			onChange={() => toggleVariableItem(item.id)}
																			onClick={(e) => e.stopPropagation()}
																			className="budget-forecast__variable-checkbox"
																			title="Mark as variable for scenario testing"
																		/>
																		{item.name}
																		{isVariable && (
																			<input
																				type="number"
																				value={Math.abs(currentValue)}
																				onChange={(e) => setVariableValue(item.id, -Math.abs(parseFloat(e.target.value) || 0))}
																				onClick={(e) => e.stopPropagation()}
																				className="budget-forecast__variable-input"
																				title="Override monthly value (expenses are negative)"
																			/>
																		)}
																	</td>
																	{columns.map((col) => {
																		if (col.type === "month") {
																			if (isMonthClosed(col.month.dateKey)) {
																				return (
																					<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
																						—
																					</td>
																				);
																			}
																			const forecastValue = getEffectiveItemValue(item, col.month.dateKey);
																			// Check if this is a large-ticket item with live data
																			if (item.is_large_ticket) {
																				const liveData = getLiveItemData(item.id, col.month.dateKey, forecastValue);
																				if (liveData.hasData) {
																					const displayRemaining = forecastValue >= 0 ? liveData.remaining : -liveData.remaining;
																					const isPending = liveData.remaining > 0;
																					return (
																						<td
																							key={col.month.dateKey}
																							className={`budget-forecast__td-value ${isPending ? "budget-forecast__td-value--pending" : "budget-forecast__td-value--paid"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}
																							title={isPending ? `Received: ${formatCurrency(liveData.actual)} / Remaining: ${formatCurrency(liveData.remaining)}` : "Complete"}
																						>
																							{formatCurrency(displayRemaining)}
																						</td>
																					);
																				}
																			}
																			const displayValue = getProratedValue(forecastValue, col.month.dateKey);
																			const standout = isStandout(forecastValue, mode);
																			return (
																				<td
																					key={col.month.dateKey}
																					className={`budget-forecast__td-value budget-forecast__td-value--expense ${standout ? "budget-forecast__td-value--standout" : ""} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}
																				>
																					{formatCurrency(displayValue)}
																				</td>
																			);
																		} else {
																			// For year totals, use remaining amounts for large-ticket items
																			const yearTotal = col.months
																				.filter(m => !isMonthClosed(m.dateKey))
																				.reduce((sum, m) => {
																					const fv = getEffectiveItemValue(item, m.dateKey);
																					if (item.is_large_ticket) {
																						const liveData = getLiveItemData(item.id, m.dateKey, fv);
																						return sum + (liveData.hasData ? (fv >= 0 ? liveData.remaining : -liveData.remaining) : fv);
																					}
																					return sum + fv;
																				}, 0);
																			return (
																				<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
																					{yearTotal !== 0 ? formatCurrency(yearTotal) : "—"}
																				</td>
																			);
																		}
																	})}
																</tr>
															);
														})}
												</React.Fragment>
											);
										})}
							</React.Fragment>
						))}

					{/* Income Sections */}
					{Object.entries(groupedItems)
						.filter(([_, data]) => data.isIncome)
						.sort(([a], [b]) => {
							const aIndex = SECTION_ORDER.indexOf(a);
							const bIndex = SECTION_ORDER.indexOf(b);
							return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
						})
						.map(([section, sectionData]) => (
							<React.Fragment key={section}>
								{/* Section Row */}
								<tr
									className={`budget-forecast__row budget-forecast__row--section budget-forecast__row--income ${collapsedSections[section] ? "budget-forecast__row--collapsed" : ""}`}
									onClick={() => toggleSection(section)}
								>
									<td className="budget-forecast__td-name">
										{collapsedSections[section] ? <FiChevronRight /> : <FiChevronDown />}
										{section}
									</td>
									{columns.map((col) => {
										if (col.type === "month") {
											if (isMonthClosed(col.month.dateKey)) {
												return (
													<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
														—
													</td>
												);
											}
											// Calculate section total, accounting for large-ticket live data
											// Large-ticket items with live data should NOT be prorated (already actual remaining)
											let regularTotal = 0;
											let largeTicketTotal = 0;
											Object.values(sectionData.categories).flat().forEach((item) => {
												const forecastValue = getEffectiveItemValue(item, col.month.dateKey);
												if (item.is_large_ticket) {
													const liveData = getLiveItemData(item.id, col.month.dateKey, forecastValue);
													if (liveData.hasData) {
														// Large-ticket with live data - don't prorate
														largeTicketTotal += forecastValue >= 0 ? liveData.remaining : -liveData.remaining;
													} else {
														// Large-ticket without live data - treat as regular (prorate)
														regularTotal += forecastValue;
													}
												} else {
													regularTotal += forecastValue;
												}
											});
											// Add Business Takehome to Income section total
											const expenses = getBusinessExpenses(col.month.dateKey);
											const invoiceNet = getEffectiveGross(col.month.dateKey) - expenses;
											const taxes = invoiceNet > 0 ? invoiceNet * taxRate : 0;
											const takehome = invoiceNet - taxes;
											// Only prorate regular items, not large-ticket items with live data or business takehome
											const proratedSectionTotal = getProratedValue(regularTotal, col.month.dateKey) + largeTicketTotal;
											return (
												<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--income ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}>
													{formatCurrency(proratedSectionTotal + takehome)}
												</td>
											);
										} else {
											const openMonths = col.months.filter(m => !isMonthClosed(m.dateKey));
											if (openMonths.length === 0) {
												return (
													<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
														—
													</td>
												);
											}
											// Calculate year item total with large-ticket handling
											const allItems = Object.values(sectionData.categories).flat();
											const yearItemTotal = openMonths.reduce((sum, m) => {
												return sum + allItems.reduce((itemSum, item) => {
													const forecastValue = getEffectiveItemValue(item, m.dateKey);
													if (item.is_large_ticket) {
														const liveData = getLiveItemData(item.id, m.dateKey, forecastValue);
														return itemSum + (liveData.hasData ? (forecastValue >= 0 ? liveData.remaining : -liveData.remaining) : forecastValue);
													}
													return itemSum + forecastValue;
												}, 0);
											}, 0);
											const yearTakehome = openMonths.reduce((sum, m) => {
												const expenses = getBusinessExpenses(m.dateKey);
												const invoiceNet = getEffectiveGross(m.dateKey) - expenses;
												const taxes = invoiceNet > 0 ? invoiceNet * taxRate : 0;
												return sum + (invoiceNet - taxes);
											}, 0);
											return (
												<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
													{formatCurrency(yearItemTotal + yearTakehome)}
												</td>
											);
										}
									})}
								</tr>

								{/* Categories and Items */}
								{!collapsedSections[section] &&
									Object.entries(sectionData.categories)
										.sort(([a], [b]) => {
											const aIndex = CATEGORY_ORDER.indexOf(a);
											const bIndex = CATEGORY_ORDER.indexOf(b);
											return (aIndex === -1 ? 999 : aIndex) - (bIndex === -1 ? 999 : bIndex);
										})
										.map(([category, items]) => {
											// Calculate mode for standout detection
											const itemModes = items.map((item) => {
												const values = months.map((m) => getEffectiveItemValue(item, m.dateKey));
												return { item, mode: getModeValue(values) };
											});

											return (
												<React.Fragment key={`${section}-${category}`}>
													{/* Category Row */}
													<tr
														className={`budget-forecast__row budget-forecast__row--category budget-forecast__row--income ${collapsedCategories[`${section}-${category}`] ? "budget-forecast__row--collapsed" : ""}`}
														onClick={() => toggleCategory(`${section}-${category}`)}
													>
														<td className="budget-forecast__td-name">
															{collapsedCategories[`${section}-${category}`] ? <FiChevronRight /> : <FiChevronDown />}
															{category}
														</td>
														{columns.map((col) => {
															if (col.type === "month") {
																if (isMonthClosed(col.month.dateKey)) {
																	return (
																		<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
																			—
																		</td>
																	);
																}
																// Calculate category total, accounting for large-ticket live data
															// Large-ticket items with live data should NOT be prorated (already actual remaining)
															let regularTotal = 0;
															let largeTicketTotal = 0;
															items.forEach((item) => {
																const forecastValue = getEffectiveItemValue(item, col.month.dateKey);
																if (item.is_large_ticket) {
																	const liveData = getLiveItemData(item.id, col.month.dateKey, forecastValue);
																	if (liveData.hasData) {
																		// Large-ticket with live data - don't prorate
																		largeTicketTotal += forecastValue >= 0 ? liveData.remaining : -liveData.remaining;
																	} else {
																		// Large-ticket without live data - treat as regular (prorate)
																		regularTotal += forecastValue;
																	}
																} else {
																	regularTotal += forecastValue;
																}
															});
															// Only prorate regular items, not large-ticket items with live data
															const displayValue = getProratedValue(regularTotal, col.month.dateKey) + largeTicketTotal;
																return (
																	<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--income ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}>
																		{formatCurrency(displayValue)}
																	</td>
																);
															} else {
																// Year total accounting for large-ticket live data
																const yearTotal = col.months
																	.filter(m => !isMonthClosed(m.dateKey))
																	.reduce((sum, m) => {
																		return sum + items.reduce((itemSum, item) => {
																			const forecastValue = getEffectiveItemValue(item, m.dateKey);
																			if (item.is_large_ticket) {
																				const liveData = getLiveItemData(item.id, m.dateKey, forecastValue);
																				return itemSum + (liveData.hasData ? (forecastValue >= 0 ? liveData.remaining : -liveData.remaining) : forecastValue);
																			}
																			return itemSum + forecastValue;
																		}, 0);
																	}, 0);
																return (
																	<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
																		{yearTotal !== 0 ? formatCurrency(yearTotal) : "—"}
																	</td>
																);
															}
														})}
													</tr>

													{/* Item Rows */}
													{!collapsedCategories[`${section}-${category}`] &&
														itemModes.map(({ item, mode }) => {
															const isVariable = variableItems.has(item.id);
															const currentValue = variableValues[item.id] ?? item.monthlyValues[months[0]?.dateKey] ?? 0;
															return (
																<tr
																	key={item.id}
																	className={`budget-forecast__row budget-forecast__row--item budget-forecast__row--income ${isVariable ? "budget-forecast__row--variable" : ""}`}
																>
																	<td className="budget-forecast__td-name">
																		<input
																			type="checkbox"
																			checked={isVariable}
																			onChange={() => toggleVariableItem(item.id)}
																			onClick={(e) => e.stopPropagation()}
																			className="budget-forecast__variable-checkbox"
																			title="Mark as variable for scenario testing"
																		/>
																		{item.name}
																		{isVariable && (
																			<input
																				type="number"
																				value={Math.abs(currentValue)}
																				onChange={(e) => setVariableValue(item.id, Math.abs(parseFloat(e.target.value) || 0))}
																				onClick={(e) => e.stopPropagation()}
																				className="budget-forecast__variable-input"
																				title="Override monthly value"
																			/>
																		)}
																	</td>
																	{columns.map((col) => {
																		if (col.type === "month") {
																			if (isMonthClosed(col.month.dateKey)) {
																				return (
																					<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
																						—
																					</td>
																				);
																			}
																			const forecastValue = getEffectiveItemValue(item, col.month.dateKey);
																			// Check if this is a large-ticket item with live data
																			if (item.is_large_ticket) {
																				const liveData = getLiveItemData(item.id, col.month.dateKey, forecastValue);
																				if (liveData.hasData) {
																					const displayRemaining = forecastValue >= 0 ? liveData.remaining : -liveData.remaining;
																					const isPending = liveData.remaining > 0;
																					return (
																						<td
																							key={col.month.dateKey}
																							className={`budget-forecast__td-value ${isPending ? "budget-forecast__td-value--pending" : "budget-forecast__td-value--paid"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}
																							title={isPending ? `Received: ${formatCurrency(liveData.actual)} / Remaining: ${formatCurrency(liveData.remaining)}` : "Complete"}
																						>
																							{formatCurrency(displayRemaining)}
																						</td>
																					);
																				}
																			}
																			const displayValue = getProratedValue(forecastValue, col.month.dateKey);
																			const standout = isStandout(forecastValue, mode);
																			return (
																				<td
																					key={col.month.dateKey}
																					className={`budget-forecast__td-value budget-forecast__td-value--income ${standout ? "budget-forecast__td-value--standout" : ""} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}
																				>
																					{formatCurrency(displayValue)}
																				</td>
																			);
																		} else {
																			// For year totals, use remaining amounts for large-ticket items
																			const yearTotal = col.months
																				.filter(m => !isMonthClosed(m.dateKey))
																				.reduce((sum, m) => {
																					const fv = getEffectiveItemValue(item, m.dateKey);
																					if (item.is_large_ticket) {
																						const liveData = getLiveItemData(item.id, m.dateKey, fv);
																						return sum + (liveData.hasData ? (fv >= 0 ? liveData.remaining : -liveData.remaining) : fv);
																					}
																					return sum + fv;
																				}, 0);
																			return (
																				<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
																					{yearTotal !== 0 ? formatCurrency(yearTotal) : "—"}
																				</td>
																			);
																		}
																	})}
																</tr>
															);
														})}
												</React.Fragment>
											);
										})}

								{/* Business Takehome Row - nested under Income section */}
								{/* Uses dynamic business expenses with proration and large-ticket handling */}
								{!collapsedSections[section] && (
									<tr className="budget-forecast__row budget-forecast__row--item budget-forecast__row--income">
										<td className="budget-forecast__td-name">Business Takehome</td>
										{columns.map((col) => {
											if (col.type === "month") {
												if (isMonthClosed(col.month.dateKey)) {
													return (
														<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
															—
														</td>
													);
												}
												const expenses = getBusinessExpenses(col.month.dateKey);
												const net = getEffectiveGross(col.month.dateKey) - expenses;
												const taxes = net > 0 ? net * taxRate : 0;
												const takehome = net - taxes;
												const estimated = isUsingEstimate(col.month.dateKey);
												return (
													<td
														key={col.month.dateKey}
														className={`budget-forecast__td-value budget-forecast__td-value--income ${estimated ? "budget-forecast__td-value--estimated" : ""} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}
														title={`Gross: ${formatCurrency(getEffectiveGross(col.month.dateKey))} | Expenses: ${formatCurrency(expenses)}`}
													>
														{formatCurrency(takehome)}
													</td>
												);
											} else {
												const openMonths = col.months.filter(m => !isMonthClosed(m.dateKey));
												if (openMonths.length === 0) {
													return (
														<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
															—
														</td>
													);
												}
												const yearTakehome = openMonths.reduce((sum, m) => {
													const expenses = getBusinessExpenses(m.dateKey);
													const net = getEffectiveGross(m.dateKey) - expenses;
													const taxes = net > 0 ? net * taxRate : 0;
													return sum + (net - taxes);
												}, 0);
												return (
													<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
														{formatCurrency(yearTakehome)}
													</td>
												);
											}
										})}
									</tr>
								)}
							</React.Fragment>
						))}

					{/* Totals Header */}
					<tr className="budget-forecast__row budget-forecast__row--totals-header">
						<td className="budget-forecast__td-name">Totals</td>
						{columns.map((col) => (
							<td key={col.type === "month" ? col.month.dateKey : `year-${col.year}`} className={`budget-forecast__td-value ${col.type === "year-total" ? "budget-forecast__td-value--year" : ""}`}></td>
						))}
					</tr>

					{/* Totals */}
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Income</td>
						{columns.map((col) => {
							if (col.type === "month") {
								if (isMonthClosed(col.month.dateKey)) {
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
											—
										</td>
									);
								}
								const calculated = getCalculatedTotals(col.month.dateKey);
								// For current month: only prorate incomeRegular, incomeFixed stays full
								let displayValue;
								if (isCurrentMonth(col.month.dateKey)) {
									const incomeRegular = calculated?.incomeRegular ?? 0;
									const incomeFixed = calculated?.incomeFixed ?? 0;
									displayValue = (incomeRegular * monthRemaining) + incomeFixed;
								} else {
									displayValue = calculated?.income ?? 0;
								}
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--income ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}>
										{formatCurrency(displayValue)}
									</td>
								);
							} else {
								const openMonths = col.months.filter(m => !isMonthClosed(m.dateKey));
								const yearTotal = getYearCalculatedTotal(openMonths, "income");
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
										{openMonths.length > 0 ? formatCurrency(yearTotal) : "—"}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Expenses</td>
						{columns.map((col) => {
							if (col.type === "month") {
								if (isMonthClosed(col.month.dateKey)) {
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
											—
										</td>
									);
								}
								const calculated = getCalculatedTotals(col.month.dateKey);
								// For current month: only prorate expenseRegular, expenseFixed stays full
								let displayValue;
								if (isCurrentMonth(col.month.dateKey)) {
									const expenseRegular = calculated?.expenseRegular ?? 0;
									const expenseFixed = calculated?.expenseFixed ?? 0;
									displayValue = (expenseRegular * monthRemaining) + expenseFixed;
								} else {
									displayValue = calculated?.expenses ?? 0;
								}
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}>
										{formatCurrency(displayValue)}
									</td>
								);
							} else {
								const openMonths = col.months.filter(m => !isMonthClosed(m.dateKey));
								const yearTotal = getYearCalculatedTotal(openMonths, "expenses");
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
										{openMonths.length > 0 ? formatCurrency(yearTotal) : "—"}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total budget-forecast__row--net">
						<td className="budget-forecast__td-name">Net</td>
						{columns.map((col) => {
							if (col.type === "month") {
								if (isMonthClosed(col.month.dateKey)) {
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
											—
										</td>
									);
								}
								const calculated = getCalculatedTotals(col.month.dateKey);
								// For current month: only prorate regularNet, fixedNet stays full
								let displayValue;
								if (isCurrentMonth(col.month.dateKey)) {
									const regularNet = calculated?.regularNet ?? 0;
									const fixedNet = calculated?.fixedNet ?? 0;
									displayValue = (regularNet * monthRemaining) + fixedNet;
								} else {
									displayValue = calculated?.net ?? 0;
								}
								return (
									<td
										key={col.month.dateKey}
										className={`budget-forecast__td-value ${displayValue >= 0 ? "budget-forecast__td-value--income" : "budget-forecast__td-value--expense"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}
									>
										{formatCurrency(displayValue)}
									</td>
								);
							} else {
								const openMonths = col.months.filter(m => !isMonthClosed(m.dateKey));
								const yearTotal = getYearCalculatedTotal(openMonths, "net");
								return (
									<td key={`year-${col.year}`} className={`budget-forecast__td-value budget-forecast__td-value--year ${yearTotal >= 0 ? "budget-forecast__td-value--income" : "budget-forecast__td-value--expense"}`}>
										{openMonths.length > 0 ? formatCurrency(yearTotal) : "—"}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total budget-forecast__row--balance">
						<td className="budget-forecast__td-name">Balance</td>
						{(() => {
							let runningBalance = startingBalance || 0;
							return columns.map((col) => {
								if (col.type === "month") {
									// For closed months, use actual net worth if available
									if (isMonthClosed(col.month.dateKey)) {
										const actualNetworth = networthByMonth[col.month.dateKey];
										if (actualNetworth !== undefined) {
											// Display actual networth but don't modify runningBalance
											// Projections should build from startingBalance, not historical networth
											return (
												<td
													key={col.month.dateKey}
													className={`budget-forecast__td-value budget-forecast__td-value--networth ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}
												>
													{formatCurrency(actualNetworth)}
												</td>
											);
										}
										return (
											<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
												—
											</td>
										);
									}
									const calculated = getCalculatedTotals(col.month.dateKey);
									// For current month: only prorate regularNet, fixedNet stays full
									let monthNet;
									if (isCurrentMonth(col.month.dateKey)) {
										const regularNet = calculated?.regularNet ?? 0;
										const fixedNet = calculated?.fixedNet ?? 0;
										monthNet = (regularNet * monthRemaining) + fixedNet;
									} else {
										monthNet = calculated?.net ?? 0;
									}
									const safeNet = isNaN(monthNet) ? 0 : monthNet;
									runningBalance += safeNet;
									// Extra safety check
									if (isNaN(runningBalance)) runningBalance = startingBalance || 0;
									return (
										<td
											key={col.month.dateKey}
											className={`budget-forecast__td-value ${runningBalance >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getInProgressClass(col.month.dateKey)}`}
										>
											{formatCurrency(runningBalance)}
										</td>
									);
								} else {
									// Year total column shows end-of-year balance
									// Check if any month in this year has data (either actual or forecast)
									const hasAnyData = col.months.some(m => !isMonthClosed(m.dateKey) || networthByMonth[m.dateKey] !== undefined);
									return (
										<td key={`year-${col.year}`} className={`budget-forecast__td-value budget-forecast__td-value--year ${runningBalance >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"}`}>
											{hasAnyData ? formatCurrency(runningBalance) : "—"}
										</td>
									);
								}
							});
						})()}
					</tr>

					{/* Actuals Section - only for closed months */}
					<tr className="budget-forecast__row budget-forecast__row--totals-header">
						<td className="budget-forecast__td-name">Actuals (Closed Months)</td>
						{columns.map((col) => (
							<td key={col.type === "month" ? col.month.dateKey : `year-${col.year}`} className={`budget-forecast__td-value ${col.type === "year-total" ? "budget-forecast__td-value--year" : ""}`}></td>
						))}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Home (Final)</td>
						{columns.map((col) => {
							if (col.type === "month") {
								const actual = getActualSpending(col.month.dateKey, "Home");
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${actual !== null ? getActualClass(col.month.dateKey) : ""}`}>
										{actual !== null ? formatCurrency(-actual) : "—"}
									</td>
								);
							} else {
								const yearTotal = col.months.reduce((sum, m) => {
									const actual = getActualSpending(m.dateKey, "Home");
									return sum + (actual !== null ? actual : 0);
								}, 0);
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
										{formatCurrency(-yearTotal)}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Apartment (Final)</td>
						{columns.map((col) => {
							if (col.type === "month") {
								const actual = getActualSpending(col.month.dateKey, "Apartment");
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${actual !== null ? getActualClass(col.month.dateKey) : ""}`}>
										{actual !== null ? formatCurrency(-actual) : "—"}
									</td>
								);
							} else {
								const yearTotal = col.months.reduce((sum, m) => {
									const actual = getActualSpending(m.dateKey, "Apartment");
									return sum + (actual !== null ? actual : 0);
								}, 0);
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
										{formatCurrency(-yearTotal)}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Other (Final)</td>
						{columns.map((col) => {
							if (col.type === "month") {
								const actual = getActualSpending(col.month.dateKey, "Other");
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${actual !== null ? getActualClass(col.month.dateKey) : ""}`}>
										{actual !== null ? formatCurrency(-actual) : "—"}
									</td>
								);
							} else {
								const yearTotal = col.months.reduce((sum, m) => {
									const actual = getActualSpending(m.dateKey, "Other");
									return sum + (actual !== null ? actual : 0);
								}, 0);
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
										{formatCurrency(-yearTotal)}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Erin Income</td>
						{columns.map((col) => {
							if (col.type === "month") {
								const income = getErinIncome(col.month.dateKey);
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--income ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${income !== null ? getActualClass(col.month.dateKey) : ""}`}>
										{income !== null ? formatCurrency(income) : "—"}
									</td>
								);
							} else {
								const yearTotal = col.months.reduce((sum, m) => {
									const income = getErinIncome(m.dateKey);
									return sum + (income !== null ? income : 0);
								}, 0);
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
										{formatCurrency(yearTotal)}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Business Takehome</td>
						{columns.map((col) => {
							if (col.type === "month") {
								const takehome = getActualTakehome(col.month.dateKey);
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--income ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${takehome !== null ? getActualClass(col.month.dateKey) : ""}`}>
										{takehome !== null ? formatCurrency(takehome) : "—"}
									</td>
								);
							} else {
								const yearTotal = col.months.reduce((sum, m) => {
									const takehome = getActualTakehome(m.dateKey);
									return sum + (takehome !== null ? takehome : 0);
								}, 0);
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
										{formatCurrency(yearTotal)}
									</td>
								);
							}
						})}
					</tr>
					{/* Subtotal: All Expenses */}
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Total Expenses</td>
						{columns.map((col) => {
							if (col.type === "month") {
								if (!shouldShowActuals(col.month.dateKey)) {
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
											—
										</td>
									);
								}
								const home = actualSpendingByMonth.Home?.[col.month.dateKey] || 0;
								const apartment = actualSpendingByMonth.Apartment?.[col.month.dateKey] || 0;
								const other = actualSpendingByMonth.Other?.[col.month.dateKey] || 0;
								const total = home + apartment + other;
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--expense ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getActualClass(col.month.dateKey)}`}>
										{formatCurrency(-total)}
									</td>
								);
							} else {
								let yearTotal = 0;
								col.months.forEach((m) => {
									if (shouldShowActuals(m.dateKey)) {
										const home = actualSpendingByMonth.Home?.[m.dateKey] || 0;
										const apartment = actualSpendingByMonth.Apartment?.[m.dateKey] || 0;
										const other = actualSpendingByMonth.Other?.[m.dateKey] || 0;
										yearTotal += home + apartment + other;
									}
								});
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--expense budget-forecast__td-value--year">
										{formatCurrency(-yearTotal)}
									</td>
								);
							}
						})}
					</tr>
					{/* Subtotal: All Income */}
					<tr className="budget-forecast__row budget-forecast__row--total">
						<td className="budget-forecast__td-name">Total Income</td>
						{columns.map((col) => {
							if (col.type === "month") {
								if (!shouldShowActuals(col.month.dateKey)) {
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
											—
										</td>
									);
								}
								const erinIncome = actualSpendingByMonth.ErinIncome?.[col.month.dateKey] || 0;
								const takehome = getActualTakehome(col.month.dateKey) || 0;
								const total = erinIncome + takehome;
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value budget-forecast__td-value--income ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getActualClass(col.month.dateKey)}`}>
										{formatCurrency(total)}
									</td>
								);
							} else {
								let yearTotal = 0;
								col.months.forEach((m) => {
									if (shouldShowActuals(m.dateKey)) {
										const erinIncome = actualSpendingByMonth.ErinIncome?.[m.dateKey] || 0;
										const takehome = getActualTakehome(m.dateKey) || 0;
										yearTotal += erinIncome + takehome;
									}
								});
								return (
									<td key={`year-${col.year}`} className="budget-forecast__td-value budget-forecast__td-value--income budget-forecast__td-value--year">
										{formatCurrency(yearTotal)}
									</td>
								);
							}
						})}
					</tr>
					<tr className="budget-forecast__row budget-forecast__row--total budget-forecast__row--net">
						<td className="budget-forecast__td-name">Actual Net</td>
						{columns.map((col) => {
							if (col.type === "month") {
								if (!shouldShowActuals(col.month.dateKey)) {
									return (
										<td key={col.month.dateKey} className={`budget-forecast__td-value ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""}`}>
											—
										</td>
									);
								}
								const home = actualSpendingByMonth.Home?.[col.month.dateKey] || 0;
								const apartment = actualSpendingByMonth.Apartment?.[col.month.dateKey] || 0;
								const other = actualSpendingByMonth.Other?.[col.month.dateKey] || 0;
								const erinIncome = actualSpendingByMonth.ErinIncome?.[col.month.dateKey] || 0;
								const takehome = getActualTakehome(col.month.dateKey) || 0;
								const totalExpenses = home + apartment + other;
								const totalIncome = erinIncome + takehome;
								const net = totalIncome - totalExpenses;
								return (
									<td key={col.month.dateKey} className={`budget-forecast__td-value ${net >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"} ${col.index % 2 === 1 ? "budget-forecast__td-value--alt" : ""} ${getActualClass(col.month.dateKey)}`}>
										{formatCurrency(net)}
									</td>
								);
							} else {
								let yearNet = 0;
								col.months.forEach((m) => {
									if (shouldShowActuals(m.dateKey)) {
										const home = actualSpendingByMonth.Home?.[m.dateKey] || 0;
										const apartment = actualSpendingByMonth.Apartment?.[m.dateKey] || 0;
										const other = actualSpendingByMonth.Other?.[m.dateKey] || 0;
										const erinIncome = actualSpendingByMonth.ErinIncome?.[m.dateKey] || 0;
										const takehome = getActualTakehome(m.dateKey) || 0;
										const totalExpenses = home + apartment + other;
										const totalIncome = erinIncome + takehome;
										yearNet += (totalIncome - totalExpenses);
									}
								});
								return (
									<td key={`year-${col.year}`} className={`budget-forecast__td-value budget-forecast__td-value--year ${yearNet >= 0 ? "budget-forecast__td-value--positive" : "budget-forecast__td-value--negative"}`}>
										{formatCurrency(yearNet)}
									</td>
								);
							}
						})}
					</tr>
				</tbody>
			</table>
		</div>
	);
};

export default BudgetDataTable;
